import type { LayoutRect, LayoutSnapshotV1 } from '@bilt/types';
export type { LayoutRect, LayoutSnapshotV1 } from '@bilt/types';

const number = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 32768;
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const rect = (v: unknown): v is LayoutRect => record(v) && number(v.x) && number(v.y) && number(v.width) && number(v.height);

/** Invalid geometry must never hide controls or create unbounded CSS dimensions. */
export function parseNativeLayout(value: unknown): LayoutSnapshotV1 | null {
  if (!record(value) || value.version !== 1 || typeof value.sceneId !== 'string' || !value.sceneId || value.sceneId.length > 128 ||
    !Number.isSafeInteger(value.revision) || Number(value.revision) < 0 || !record(value.bounds) ||
    !number(value.bounds.width) || !number(value.bounds.height) || value.bounds.width === 0 || value.bounds.height === 0 ||
    !record(value.safeInsets) || !['top', 'right', 'bottom', 'left'].every(k => number((value.safeInsets as Record<string, unknown>)[k])) ||
    !Array.isArray(value.reservedRegions) || value.reservedRegions.length > 16 || !value.reservedRegions.every(r =>
      record(r) && typeof r.id === 'string' && r.id.length <= 128 && (r.kind === 'division' || r.kind === 'occlusion') && typeof r.active === 'boolean' && rect(r.rect)) ||
    !['left', 'right', 'none'].includes(String(value.verticalBarEdge)) ||
    !Array.isArray(value.keyboardOcclusions) || value.keyboardOcclusions.length > 8 || !value.keyboardOcclusions.every(rect) ||
    !number(value.keyboardInset) || !number(value.fontScale) || value.fontScale < 0.5 || value.fontScale > 10 ||
    !record(value.capabilities) || typeof value.capabilities.reservedRegions !== 'boolean' || typeof value.capabilities.cameraDirection !== 'boolean') return null;
  const snapshot = value as LayoutSnapshotV1;
  if (snapshot.safeInsets.left + snapshot.safeInsets.right >= snapshot.bounds.width ||
    snapshot.safeInsets.top + snapshot.safeInsets.bottom >= snapshot.bounds.height) return null;
  return snapshot;
}

export function intersectRect(a: LayoutRect, b: LayoutRect): LayoutRect | null {
  const x = Math.max(a.x, b.x), y = Math.max(a.y, b.y);
  const width = Math.min(a.x + a.width, b.x + b.width) - x;
  const height = Math.min(a.y + a.height, b.y + b.height) - y;
  return width > 0 && height > 0 ? { x, y, width, height } : null;
}

function subtract(a: LayoutRect, obstruction: LayoutRect): LayoutRect[] {
  const b = intersectRect(a, obstruction);
  if (!b) return [a];
  return [
    { x: a.x, y: a.y, width: a.width, height: b.y - a.y },
    { x: a.x, y: b.y + b.height, width: a.width, height: a.y + a.height - b.y - b.height },
    { x: a.x, y: b.y, width: b.x - a.x, height: b.height },
    { x: b.x + b.width, y: b.y, width: a.x + a.width - b.x - b.width, height: b.height },
  ].filter(r => r.width > 0 && r.height > 0);
}

export type WorkspaceRegions = { mode: 'single' | 'columns' | 'rows'; primary: LayoutRect; secondary?: LayoutRect };

/** View-local free rectangles. Presentation never changes the document's content/state. */
export function workspaceRegions(frame: LayoutRect, layout: LayoutSnapshotV1 | null): WorkspaceRegions {
  const insets = layout?.safeInsets ?? { top: 0, left: 0, bottom: 0, right: 0 };
  const safe = layout ? { x: insets.left, y: insets.top, width: layout.bounds.width - insets.left - insets.right,
    height: layout.bounds.height - insets.top - insets.bottom } : frame;
  const usable = intersectRect(frame, safe) ?? { ...frame, width: 0, height: 0 };
  const exclusions = [
    ...(layout?.reservedRegions.filter(r => r.active &&
      // A bar already outside the safe content must not grow back into it.
      (r.kind === 'division' || intersectRect(usable, r.rect) !== null)).map(r => ({
      x: r.rect.x - 8, y: r.rect.y - 8, width: r.rect.width + 16, height: r.rect.height + 16,
    })) ?? []),
    ...(layout?.keyboardOcclusions ?? []),
  ];
  // Older geometry providers can report only a full-width keyboard inset.
  if (layout && !layout.keyboardOcclusions.length && layout.keyboardInset > 0)
    exclusions.push({ x: 0, y: layout.bounds.height - layout.keyboardInset, width: layout.bounds.width, height: layout.keyboardInset });
  let regions = [usable];
  exclusions.forEach(r => { regions = regions.flatMap(a => subtract(a, r)); });
  regions.sort((a, b) => b.width * b.height - a.width * a.height);
  const scale = Math.max(1, layout?.fontScale ?? 1);
  const readable = regions.filter(r => r.width >= 280 * Math.min(scale, 1.6) && r.height >= 180);
  let primary = readable[0] ?? regions[0] ?? usable;
  let secondary: LayoutRect | undefined = readable[1];
  let mode: WorkspaceRegions['mode'] = 'single';
  if (secondary) {
    const row = Math.abs(primary.x - secondary.x) < 16 && Math.abs(primary.width - secondary.width) < 32;
    const column = Math.abs(primary.y - secondary.y) < 16;
    const upper = primary.y < secondary.y ? primary : secondary;
    // A small system occlusion can leave a shallow strip on the cover display.
    // Keep one complete scroll area instead of squeezing the artwork into it.
    const shallowArtwork = row && upper.height < 300 * Math.min(scale, 1.3);
    if ((!row && !column) || shallowArtwork) secondary = undefined;
    else {
      mode = row ? 'rows' : 'columns';
      if ((row && primary.y > secondary.y) || (!row && primary.x > secondary.x)) [primary, secondary] = [secondary, primary];
    }
  }
  if (!secondary && primary.width >= 720 * scale && primary.height >= 320) {
    const secondWidth = Math.max(280 * scale, Math.min(primary.width * .36, 440 * scale));
    secondary = { x: primary.x + primary.width - secondWidth, y: primary.y, width: secondWidth, height: primary.height };
    primary = { ...primary, width: primary.width - secondWidth - 24 };
    mode = 'columns';
  }
  const local = (r: LayoutRect): LayoutRect => ({ ...r, x: r.x - frame.x, y: r.y - frame.y });
  return { mode, primary: local(primary), ...(secondary ? { secondary: local(secondary) } : {}) };
}
